import React, { useEffect, useState, useMemo, useContext, Fragment } from "react";
import useSWR from "swr";
import {
  Alert,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Modal,
  ModalBody,
} from "reactstrap";
import FalconCardHeader from "../common/FalconCardHeader";
import WizardError from "../auth/register/WizardError";
import classNames from "classnames";
import ButtonIcon from "../common/ButtonIcon";
import { Controller, useForm, useWatch } from "react-hook-form";
import NumberFormat from "react-number-format";
import FullPagination from "../common/pagination/FullPagination";
import SelectProviderListItem from "../common/SelectProviderListItem";
import { faUndo, faSearch, faCog, faSave, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import withRedirect from "../../helpers/hoc/withRedirect";
import { useParams, useLocation } from "react-router";
import { PreloadState } from "../UI/preloading/PreloadState";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import SelectPropertyListItem from "../common/SelectPropertyListItem";
import Datetime from "react-datetime";
import { fetchProviders, createProvider } from "../../helpers/api/provider";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import moment from "moment";
import CreateProviderForm from "../providers/CreateProviderForm";
import ExpensePaymentFields, { emptyExpensePayment, expensePaymentError, expensePaymentPayload } from "./ExpensePaymentFields";
import { Toast } from "../UI/toast/Toast";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { fetchAccountingAreas, createAccountingArea } from "../../helpers/api/accountingAreas";
import { fetchAccountPlans } from "../../helpers/api/accountPlans";
import { findAllCurrencies, findAllExchangeRates } from "../../helpers/api/currency";
import {
  convertToUsd,
  convertFromUsd,
  formatAmountForBackend,
  formatAmountRounded,
  getCurrencyName,
} from "../../helpers/currencyConversion.js";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { CurrencyContext } from "../../context/Context";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import CreateExhangeRateModal from "../exchange/components/CreateExchangeRateModal";
import useModuleBase from "../../helpers/hooks/useModuleBase";
import { fetchAllPages } from "../../helpers/api/pagination";

const CreateCommonCostsForm = ({
  commonCost,
  handleSubmitData,
  setRedirectUrl,
  debttopayId,
  common,
  exchangeRate,
}) => {
  const moduleBase = useModuleBase();

  const { id } = useParams();
  const history = useHistory();
  const { state } = useLocation();
  const { selectedCondominium } = useSelector((stateRedux) => stateRedux.condominium);
  const { role } = useSelector((stateRedux) => stateRedux.auth.user);
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const condominiumId = id || selectedCondominium;
  const activeExchangeRate = Number(exchangeRate ?? curr?.value ?? 0);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: {
      name: "",
      value: "",
      montoOriginal: "",
      monedaOriginal: "",
      description: "",
      accounting_area_id: "",
    },
  });

  const watchedOriginalAmount = useWatch({
    control,
    name: "montoOriginal",
  });

  const watchedOriginalCurrency = useWatch({
    control,
    name: "monedaOriginal",
    defaultValue: "",
  });

  const watchedExpenseDate = useWatch({
    control,
    name: "expired_at",
    defaultValue: "",
  });

  const [providerSearch, setProviderSearch] = useState("");
  const [params, setParams] = useState({
    condominiumId: id,
    search: null,
  });

  const applyProviderSearch = (value = providerSearch) => {
    const term = typeof value === "string" ? value.trim() : "";
    setProviderSearch(term);
    setParams((prev) => ({ ...prev, search: term || null }));
  };

  const {
    mutate,
    data,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchProviders, params);

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [createProviderModalOpen, setCreateProviderModalOpen] = useState(false);
  const [accountingAreas, setAccountingAreas] = useState([]);
  const [accountPlans, setAccountPlans] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [hasLoadedCurrencies, setHasLoadedCurrencies] = useState(false);
  const [hasInitializedCurrencyDefault, setHasInitializedCurrencyDefault] = useState(false);

  // El endpoint trae TODAS las áreas contables del condominio (de gastos y de
  // ingresos mezcladas); acá solo dejamos pasar las que apuntan a una cuenta
  // de Gastos (grupo "6"), para no mostrar áreas creadas desde el flujo de
  // ingresos.
  const expenseAccountingAreas = accountingAreas.filter((area) => {
    const code = area.account_plan?.code || area.accountPlan?.code;
    return code && code.startsWith("6");
  });
  const [createAccountAreaModalOpen, setCreateAccountAreaModalOpen] = useState(false);
  const [payment, setPayment] = useState(emptyExpensePayment);
  const [newAreaName, setNewAreaName] = useState("");
  const [selectedAccountPlanId, setSelectedAccountPlanId] = useState("");
  const [isCreatingArea, setIsCreatingArea] = useState(false);
  const [pendingAreaId, setPendingAreaId] = useState(null);

  // Registro manual: provider_id no tiene un <input> propio (se setea al
  // hacer click en un proveedor de la lista), así que sin este register()
  // react-hook-form nunca lo incluye en los values enviados al submit,
  // aunque setValue() lo actualice y se vea seleccionado en pantalla.
  useEffect(() => {
    register("provider_id");
  }, [register]);

  // useSWR para obtener datos de forma eficiente y evitar duplicados
  const { data: areasRes, mutate: mutateAreas } = useSWR(id ? [id, "accounting-areas"] : null, () => fetchAccountingAreas(id));
  const { data: plansRes } = useSWR(id ? [id, "account-plans"] : null, () => fetchAccountPlans(id));

  useEffect(() => {
    if (areasRes && areasRes.data) setAccountingAreas(areasRes.data);
  }, [areasRes]);

  useEffect(() => {
    if (plansRes && plansRes.data) setAccountPlans(plansRes.data);
  }, [plansRes]);

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      if (!condominiumId) return;

      setHasLoadedCurrencies(false);

      try {
        const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
          condominium_id: condominiumId,
        }));

        if (isMounted) {
          setAvailableCurrencies(response?.data || []);
        }
      } catch (error) {
        if (isMounted) {
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setHasLoadedCurrencies(true);
        }
      }
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, [condominiumId]);

  const currencyCatalog = useMemo(() => {
    const normalized = (availableCurrencies || []).map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackUsd: false,
    }));

    const hasUsd = normalized.some((item) => item.iso === baseCurrency.iso);
    if (!hasUsd) {
      normalized.push({
        id: baseCurrency.iso,
        iso: baseCurrency.iso,
        name: getCurrencyName(baseCurrency.iso),
        isFallbackUsd: true,
      });
    }

    return normalized;
  }, [availableCurrencies, baseCurrency.iso]);

  const preferredDefaultCurrency = useMemo(() => {
    const bolivaresCurrency = currencyCatalog.find((item) => {
      const name = (item?.name || "").toLowerCase();
      const iso = (item?.iso || "").toLowerCase();
      const symbol = (item?.symbol || "").toLowerCase();

      return name.includes("boliva") || iso === "ves" || symbol === "bs" || iso === "bs";
    });

    const fallbackByBase = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );

    return bolivaresCurrency || fallbackByBase || currencyCatalog[0] || null;
  }, [baseCurrency.iso, currencyCatalog]);

  const selectedCurrencyData = useMemo(() => {
    const currentValue = String(watchedOriginalCurrency || "");
    return (currencyCatalog || []).find(
      (item) =>
        String(item.id) === currentValue ||
        item.iso === currentValue.toUpperCase()
    );
  }, [currencyCatalog, watchedOriginalCurrency]);

  const isSelectedCurrencyUsd =
    (selectedCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

  useEffect(() => {
    if (!hasLoadedCurrencies || hasInitializedCurrencyDefault || !preferredDefaultCurrency) {
      return;
    }

    const preferredCurrencyId = String(preferredDefaultCurrency.id);
    const currentCurrencyId = String(watchedOriginalCurrency || "");

    if (currentCurrencyId !== preferredCurrencyId) {
      setValue("monedaOriginal", preferredCurrencyId, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }

    setHasInitializedCurrencyDefault(true);
  }, [
    baseCurrency.iso,
    hasInitializedCurrencyDefault,
    hasLoadedCurrencies,
    preferredDefaultCurrency,
    setValue,
    watchedOriginalCurrency,
  ]);

  const parsedExpenseDate = useMemo(() => {
    if (!watchedExpenseDate) {
      return null;
    }

    const parsedDate = moment(watchedExpenseDate);
    return parsedDate.isValid() ? parsedDate : null;
  }, [watchedExpenseDate]);

  useEffect(() => {
    if (!hasLoadedCurrencies || !currencyCatalog.length) {
      return;
    }

    if (hasInitializedCurrencyDefault || watchedOriginalCurrency) {
      return;
    }

    const fallback = preferredDefaultCurrency;

    if (fallback) {
      setValue("monedaOriginal", String(fallback.id), {
        shouldDirty: false,
        shouldValidate: true,
      });
      setHasInitializedCurrencyDefault(true);
    }
  }, [currencyCatalog, hasLoadedCurrencies, preferredDefaultCurrency, setValue, watchedOriginalCurrency, hasInitializedCurrencyDefault]);

  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const canRegisterRate = role === "manager" || role === "root";

  const {
    rateByDate,
    rateResolution,
    isRateLoading,
    appliedExchangeRate,
    bumpRateRefreshTick,
  } = useExchangeRateForDate({
    currencyId: selectedCurrencyData?.id,
    isUsdCurrency: isSelectedCurrencyUsd || Boolean(selectedCurrencyData?.isFallbackUsd),
    date: parsedExpenseDate,
    activeExchangeRate,
    condominiumId: condominiumId || selectedCondominium,
  });

  // HO-TASK-253: la tasa en moneda local (Bs) de la fecha del gasto, para
  // guardarla como `rate` y mostrar el equivalente aunque el monto se cargue
  // en dólares. Si la moneda elegida ya es la local, la resuelve el hook de
  // arriba y este se apaga (currencyId nulo) para no pedir ni registrar la
  // misma tasa dos veces.
  const localCurrencyId = curr?.currency?.id;
  const isSelectedCurrencyLocal =
    Boolean(localCurrencyId) && String(selectedCurrencyData?.id) === String(localCurrencyId);
  const { appliedExchangeRate: localRateByDate, isRateLoading: isLocalRateLoading } =
    useExchangeRateForDate({
      currencyId: isSelectedCurrencyLocal ? null : localCurrencyId,
      isUsdCurrency: false,
      date: parsedExpenseDate,
      activeExchangeRate: null,
      condominiumId: condominiumId || selectedCondominium,
    });
  const rateForExpenseDate = isSelectedCurrencyLocal ? appliedExchangeRate : localRateByDate;

  const isSelectedDateToday =
    Boolean(parsedExpenseDate) && parsedExpenseDate.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");

  const handleCreateAccountingArea = async () => {
    // Sin este guard, varios clicks rápidos disparan varios POST antes de que
    // el primero responda, creando la misma área duplicada (mismo código,
    // mismo nombre, mismo segundo de creación).
    if (!newAreaName || isCreatingArea) return;
    setIsCreatingArea(true);
    try {
      // Intentar encontrar el plan de Gastos Operativos (6.1) por defecto si no hay uno seleccionado
      let planId = selectedAccountPlanId;
      if (!planId) {
        const defaultPlan = accountPlans.find(p => p.code === '6.1');
        planId = defaultPlan ? defaultPlan.id : null;
      }

      const response = await createAccountingArea({
        condominium_id: id,
        name: newAreaName,
        account_plan_id: planId,
      });
      if (response.code === SUCCESS_RESPONSE.OK || response.code === SUCCESS_RESPONSE.CREATED) {
        Toast.fire({
          type: "success",
          title: "Área creada con éxito",
        });
        setNewAreaName("");
        setSelectedAccountPlanId("");
        setCreateAccountAreaModalOpen(false);
        setPendingAreaId(response.data?.id ?? null);
        mutateAreas();
      }
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsCreatingArea(false);
    }
  };

  const renderProvidersItems = () => {
    return data.map((provider) => {
      return (
        <SelectProviderListItem
          avatar={"https://picsum.photos/200/200"}
          onSelect={() => {
            if (!commonCost) {
              setSelectedProvider(provider.id);
              setValue("provider_id", provider.id, { shouldValidate: true });
            }
          }}
          /*             onSelect={setSelectedProperty} */
          selected={selectedProvider}
          id={provider.id}
          key={provider.id}
          title={provider.name}
          toDeleteId={provider.id}
          toEditId={provider.id}
          createdAt={provider.created_at}
          alternativeName="proveedor"
        />
      );
    });
  };

  const renderProvidersItemsAdd = () => {
    return data.map((provider) => {
      return (
        <SelectPropertyListItem
          avatar={"https://picsum.photos/200/200"}
          onSelect={() => {
            setSelectedProvider(provider.id);
            setValue("provider_id", provider.id, { shouldValidate: true });
          }}
          selected={selectedProvider}
          id={provider.id}
          key={provider.id}
          title={provider.name}
          toDeleteId={provider.id}
          toEditId={provider.id}
          createdAt={provider.created_at}
          alternativeName="proveedor"
        />
      );
    });
  };

  useEffect(() => {
    if (!state?.type_id || !state?.type_name) {
      history.push(`/dashboard/finance`);
    }
  }, [state]);

  // No se requiere cálculo automático ya que el monto se ingresa directamente.

  useEffect(() => {
    const convertedValue = convertToUsd(
      watchedOriginalAmount,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    setValue("value", convertedValue === "" ? "" : String(convertedValue));
  }, [appliedExchangeRate, isSelectedCurrencyUsd, setValue, watchedOriginalAmount]);

  useEffect(() => {
    if (common && accountingAreas.length > 0) {
      const areaId = common.sub_expenses?.[0]?.accounting_area?.id || "";
      setValue("accounting_area_id", areaId);
    }
  }, [common, accountingAreas, setValue]);

  useEffect(() => {
    if (pendingAreaId && expenseAccountingAreas.some((area) => area.id === pendingAreaId)) {
      setValue("accounting_area_id", pendingAreaId, { shouldValidate: true });
      setPendingAreaId(null);
    }
  }, [accountingAreas, pendingAreaId, setValue]);

  useEffect(() => {
    const defaultValues = {};
    defaultValues.name = common?.name;
    defaultValues.description = common?.description;
    defaultValues.type_payment_supplier = common?.type_payment_supplier;
    defaultValues.expired_at = common?.expired_at || "";

    if (common) {
      defaultValues.monedaOriginal = common?.monedaOriginal
        ? String(common.monedaOriginal)
        : curr?.currency?.id
          ? String(curr.currency.id)
          : "";
      defaultValues.montoOriginal = common?.montoOriginal ?? common.value;
      defaultValues.value = common.value;
      defaultValues.accounting_area_id = common.sub_expenses?.[0]?.accounting_area?.id || "";
    } else {
      defaultValues.monedaOriginal = curr?.currency?.id
        ? String(curr.currency.id)
        : "";
    }

    reset({ ...defaultValues });
    // eslint-disable-next-line
  }, [common, curr, reset]);

  useEffect(() => {
    if (!common || !currencyCatalog.length || selectedCurrencyData) {
      return;
    }

    const currentMoneda = String(watch("monedaOriginal") || "");
    if (!currentMoneda) {
      return;
    }

    const mappedByIso = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === currentMoneda.toUpperCase()
    );

    if (mappedByIso) {
      setValue("monedaOriginal", String(mappedByIso.id), {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [common, currencyCatalog, selectedCurrencyData, setValue, watch]);

  const handleCreateProviderSubmit = async (values) => {
    try {
      const response = await createProvider(id, values);
      const isSuccess =
        response?.code === SUCCESS_RESPONSE.CREATED ||
        response?.code === SUCCESS_RESPONSE.OK ||
        response?.data?.id;

      if (isSuccess) {
        setCreateProviderModalOpen(false);
        Toast.fire({
          type: "success",
          title: "Se ha creado el proveedor satisfactoriamente.",
        });
        const createdId = response?.data?.id;
        if (createdId) {
          setSelectedProvider(createdId);
          setValue("provider_id", createdId, { shouldValidate: true });
          applyProviderSearch(response.data.name);
        }
        mutate();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const resolveRateForCurrency = async (currencyData) => {
    if (!currencyData) {
      return null;
    }

    const isUsdCurrency =
      (currencyData?.iso || "").toUpperCase() === baseCurrency.iso ||
      currencyData?.isFallbackUsd;

    if (isUsdCurrency) {
      return 1;
    }

    try {
      const response = await fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, {
        currency: currencyData.id,
      }));

      const rates = response?.data || [];
      const selectedDateKey = parsedExpenseDate?.format("YYYY-MM-DD");
      const rateForDay = selectedDateKey
        ? rates
          .filter(
            (rate) =>
              moment(rate.date || rate.created_at).format("YYYY-MM-DD") ===
              selectedDateKey
          )
          .sort(
            (a, b) =>
              moment(b.date || b.created_at).valueOf() -
              moment(a.date || a.created_at).valueOf()
          )[0]
        : null;

      if (selectedDateKey && !rateForDay) {
        return 0;
      }

      return Number(rateForDay?.value || appliedExchangeRate || activeExchangeRate || 0);
    } catch (error) {
      return Number(appliedExchangeRate || activeExchangeRate || 0);
    }
  };

  const handleCurrencyChange = async (event) => {
    const nextCurrency = event.target.value;
    const nextCurrencyData = (currencyCatalog || []).find(
      (item) => String(item.id) === String(nextCurrency)
    );
    const currentCurrencyData = selectedCurrencyData;

    if (!nextCurrencyData || !currentCurrencyData) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    const currentIsUsd =
      (currentCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;
    const nextIsUsd =
      (nextCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

    if (!currentIsUsd && !nextIsUsd) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    let nextOriginalAmount = watchedOriginalAmount;

    if (currentIsUsd && !nextIsUsd) {
      const targetRate = await resolveRateForCurrency(nextCurrencyData);

      if (!targetRate || targetRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertFromUsd(
        watchedOriginalAmount,
        false,
        targetRate
      );
    } else if (!currentIsUsd && nextIsUsd) {
      const sourceRate =
        Number(appliedExchangeRate || 0) > 0 && !rateResolution?.isFallback
          ? Number(appliedExchangeRate)
          : await resolveRateForCurrency(currentCurrencyData);

      if (!sourceRate || sourceRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertToUsd(
        watchedOriginalAmount,
        false,
        sourceRate
      );
    }

    setValue("monedaOriginal", nextCurrency, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue(
      "montoOriginal",
      nextOriginalAmount === "" ? "" : String(nextOriginalAmount),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const handleFormSubmit = async (values) => {
    if (!values.expired_at) {
      Toast.fire({
        type: "error",
        title: "La fecha del gasto es obligatoria.",
      });
      return;
    }

    if (!isSelectedCurrencyUsd && (!appliedExchangeRate || appliedExchangeRate <= 0 || rateResolution?.isFallback)) {
      Toast.fire({
        type: "error",
        title: "No hay una tasa del dia disponible para convertir esta moneda a USD.",
      });
      return;
    }

    const normalizedUsdValue = convertToUsd(
      values.montoOriginal,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    const paymentError = expensePaymentError(payment, normalizedUsdValue);
    if (paymentError) {
      Toast.fire({ type: "error", title: paymentError });
      return;
    }

    await handleSubmitData({
      ...values,
      ...expensePaymentPayload(payment, normalizedUsdValue),
      value: normalizedUsdValue === "" ? "" : formatAmountForBackend(normalizedUsdValue),
      // HO-TASK-253: la tasa que se guarda con el gasto es la de SU fecha en
      // moneda local, se haya cargado en bolívares o en dólares. Si no se
      // pudo resolver va vacía y el backend la completa con la registrada
      // para esa fecha.
      rate: rateForExpenseDate || null,
    });
  };

  const usdEquivalent = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return null;
    }

    const convertedAmount = convertToUsd(
      watchedOriginalAmount,
      false,
      appliedExchangeRate
    );

    return convertedAmount === "" ? null : convertedAmount;
  }, [appliedExchangeRate, isSelectedCurrencyUsd, watchedOriginalAmount]);

  const helpText = useMemo(() => {
    if (!parsedExpenseDate || !parsedExpenseDate.isValid()) {
      return "Selecciona la fecha primero";
    }

    const dateLabel = parsedExpenseDate.format("DD/MM/YYYY");

    // HO-TASK-253: cargado en dólares, el equivalente se muestra en la moneda
    // local a la tasa de la fecha del gasto, que es la que se guarda.
    if (isSelectedCurrencyUsd) {
      if (isLocalRateLoading || !curr?.currency?.iso) {
        return "";
      }

      const localEquivalent = convertFromUsd(watchedOriginalAmount, false, rateForExpenseDate);

      if (localEquivalent === "") {
        return "";
      }

      return `Tasa ${dateLabel}: ${formatAmountRounded(rateForExpenseDate, 2)} | Equivalente aproximado: ${curr.currency.iso} ${formatAmountRounded(localEquivalent, 2)}`;
    }

    if (isRateLoading) {
      return "";
    }

    if (usdEquivalent === null) {
      return "";
    }

    const rateLabel = appliedExchangeRate
      ? formatAmountRounded(appliedExchangeRate, 2)
      : "N/D";

    return `Tasa ${dateLabel}: ${rateLabel} | Equivalente aproximado: ${baseCurrency.symbol}${formatAmountRounded(usdEquivalent, 2)} ${baseCurrency.iso}`;
  }, [
    appliedExchangeRate,
    isRateLoading,
    isLocalRateLoading,
    rateForExpenseDate,
    watchedOriginalAmount,
    curr,
    isSelectedCurrencyUsd,
    parsedExpenseDate,
    rateResolution,
    usdEquivalent,
    baseCurrency,
  ]);

  return (
    <Card
      onSubmit={handleSubmit(handleFormSubmit)}
      className="bg-light mb-3"
      data-testid="form"
      tag={Form}
    >
      <PreloadState loading={isSubmitting} message={`Guardando ${state?.type_name || "gasto"}...`}>
        <Fragment>
          <FalconCardHeader title="Detalles del gasto" light={true} />
          <CardBody>
            <FormGroup>
              <Label for="name">Nombre del gasto</Label>
              <Input
                autoComplete="off"
                id="name"
                name="name"
                onChange={(event) => {
                  setValue("name", event.target.value);
                }}
                value={watch("name")}
                innerRef={register({
                  required: "Nombre del gasto es requerido.",
                  minLength: {
                    value: 4,
                    message: "El nombre del gasto debe tener al menos 4 caracteres.",
                  },
                })}
                placeholder="Ej: Mantenimiento de áreas"
                className={classNames({
                  "border-danger": errors.name?.message,
                })}
              />
            </FormGroup>

            <Row form>
              <Col md={6}>
                <FormGroup>
                  <Label for="accounting_area_id">Cuenta / Área Contable</Label>
                  <Input
                    type="select"
                    name="accounting_area_id"
                    className={classNames({
                      "border-danger": errors.accounting_area_id,
                    })}
                    innerRef={register({
                      required: "Debes seleccionar un área contable.",
                    })}
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Selecciona una Cuenta / Área
                    </option>
                    {expenseAccountingAreas.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.name}
                      </option>
                    ))}
                  </Input>
                  {errors.accounting_area_id && (
                    <WizardError
                      error={errors.accounting_area_id.message}
                      className="fs--1 font-weight-normal d-block"
                    />
                  )}
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 mt-1"
                    onClick={() => setCreateAccountAreaModalOpen(true)}
                  >
                    <FontAwesomeIcon icon={faCog} className="mr-1" />
                    Nueva Área/Categoría
                  </Button>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="description">Descripción <span className="text-danger">*</span></Label>
                  <Input
                    autoComplete="off"
                    name="description"
                    defaultValue={common?.description}
                    onChange={(event) => {
                      setValue("description", event.target.value);
                    }}
                    value={watch("description")}
                    innerRef={register({
                      required: "La descripción es requerida.",
                    })}
                    type={"textarea"}
                    rows={1}
                    placeholder="Detalles sobre el gasto..."
                    className={classNames({
                      "border-danger": errors.description?.message,
                    })}
                  />
                  {errors.description && (
                    <span className="text-danger fs--1">{errors.description.message}</span>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Modal isOpen={createAccountAreaModalOpen} toggle={() => setCreateAccountAreaModalOpen(!createAccountAreaModalOpen)}>
              <div className="modal-header">
                <h5 className="modal-title">Crear Nueva Área / Categoría de Gasto</h5>
                <button type="button" className="close" onClick={() => setCreateAccountAreaModalOpen(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <ModalBody>
                <FormGroup>
                  <Label for="newAreaName">Nombre del Área (ej: Mantenimiento Ascensor)</Label>
                  <Input
                    type="text"
                    id="newAreaName"
                    value={newAreaName}
                    onChange={(e) => setNewAreaName(e.target.value)}
                    placeholder="Ingrese el nombre"
                  />
                </FormGroup>
                <div className="d-flex justify-content-end">
                  <Button color="secondary" className="mr-2" onClick={() => setCreateAccountAreaModalOpen(false)} disabled={isCreatingArea}>
                    Cancelar
                  </Button>
                  <Button color="primary" onClick={handleCreateAccountingArea} disabled={isCreatingArea}>
                    {isCreatingArea ? "Creando..." : "Crear Área"}
                  </Button>
                </div>
              </ModalBody>
            </Modal>

            <Row form className="pt-3">
              <Col sm tag={FormGroup}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0">Seleccione un proveedor de la lista</Label>
                </div>
                <Row>
                  <Col xs={12} md={""} className="d-flex mb-2 mb-3">
                    <Input
                      type="text"
                      value={providerSearch}
                      placeholder="Buscar por nombre, cédula, teléfono, correo, descripción o dirección..."
                      className="d-inline-block search-input mr-2"
                      onKeyPress={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          applyProviderSearch(event.target.value);
                        }
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setProviderSearch(value);
                        if (!value.trim()) {
                          setParams((prev) => ({ ...prev, search: null }));
                        }
                      }}
                    />
                    <ButtonIcon
                      icon={faSearch}
                      className="d-inline-block text-nowrap mr-2"
                      color="falcon-default"
                      type="button"
                      onClick={() => applyProviderSearch()}
                    >
                      Buscar
                    </ButtonIcon>
                    <Button
                      color="primary"
                      type="button"
                      className="d-inline-block text-nowrap"
                      onClick={() => setCreateProviderModalOpen(true)}
                    >
                      Nuevo proveedor
                    </Button>
                  </Col>
                </Row>
                <Row className="m-0 p-0">
                  <Col sm={12} tag={FormGroup} className="m-0 p-0">
                    {!debttopayId
                      ? renderProvidersItems()
                      : renderProvidersItemsAdd()}
                  </Col>
                </Row>
                <FullPagination
                  nextPage={nextPage}
                  prevPage={previousPage}
                  goToPage={goToPage}
                  meta={meta}
                  className={"shadow-none"}
                  posText={"proveedores"}
                  perPage={perPage}
                />
              </Col>
            </Row>

            <Modal isOpen={createProviderModalOpen} toggle={() => setCreateProviderModalOpen(false)}>
              <ModalBody>
                <CreateProviderForm
                  condominiumId={id}
                  handleSubmitData={handleCreateProviderSubmit}
                  setRedirect={() => { }}
                  setRedirectUrl={() => { }}
                  onCancel={() => setCreateProviderModalOpen(false)}
                />
              </ModalBody>
            </Modal>

            <Row form>
              <Col sm={6} className="w-100">
                <FormGroup>
                  <Label for="description">Fecha del gasto <span className="text-danger">*</span></Label>
                  <Datetime
                    className={`${"w-100"} ${errors.expired_at?.message ? "border-danger" : ""}`}
                    name="expired_at"
                    innerRef={register("expired_at", {
                      required: "La fecha del gasto es obligatoria.",
                    })}
                    value={watch("expired_at")}
                    onChange={(d) => {
                      const formattedDate = moment(d).format(
                        "YYYY-MM-DD HH:mm:ss"
                      );
                      setValue("expired_at", formattedDate, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                  />
                  <WizardError
                    error={errors.expired_at?.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                </FormGroup>
              </Col>
              <Col sm={6} tag={FormGroup}>
                <Label for="montoOriginal">Monto del gasto</Label>
                <Controller
                  name="montoOriginal"
                  control={control}
                  rules={{ required: "Monto es requerido" }}
                  render={({ onChange, value }) => (
                    <>
                      <InputWithLeftSelect
                        selectValue={watchedOriginalCurrency || ""}
                        onSelectChange={handleCurrencyChange}
                        selectOptions={(currencyCatalog || []).map((item) => ({
                          value: String(item.id),
                          label: `${item.iso} (${item.name})`,
                        }))}
                        errorMessage={errors.montoOriginal?.message}
                        helpText={helpText}
                        selectAriaLabel="Moneda original"
                        selectMinWidth="120px"
                      >
                        <NumberFormat
                          customInput={Input}
                          thousandSeparator
                          prefix={
                            selectedCurrencyData?.iso
                              ? `${selectedCurrencyData.iso} `
                              : ""
                          }
                          fixedDecimalScale
                          decimalScale={2}
                          onValueChange={(vals) => onChange(vals.value)}
                          value={value}
                          placeholder="0.00"
                          className={classNames("font-weight-bold border-left-0", {
                            "border-danger": errors.montoOriginal,
                          })}
                        />
                      </InputWithLeftSelect>
                      {Boolean(parsedExpenseDate && parsedExpenseDate.isValid()) && isRateLoading && (
                        <small className="text-muted d-block mt-1">
                          <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                          Consultando tasa para la fecha...
                        </small>
                      )}
                      {!isSelectedCurrencyUsd && rateResolution?.isFallback && !isRateLoading && (
                        <Alert color="warning" className="mt-2 mb-0 py-2 fs--1">
                          No se encontró la tasa oficial registrada para la fecha{" "}
                          <strong>{moment(rateResolution.selectedDateKey).format("DD/MM/YYYY")}</strong>.
                          {canRegisterRate ? (
                            <>
                              {" "}
                              <Button
                                color="link"
                                className="p-0 fs--1 font-weight-bold text-decoration-none ml-1"
                                onClick={() => setIsRateModalOpen(true)}
                              >
                                Registrar tasa
                              </Button>
                            </>
                          ) : (
                            " Se está utilizando una tasa de referencia."
                          )}
                        </Alert>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>

            {!commonCost && (
              <ExpensePaymentFields
                condominiumId={id}
                amount={isSelectedCurrencyUsd ? Number(watchedOriginalAmount || 0) : usdEquivalent}
                value={payment}
                onChange={setPayment}
                chargedToOwners={Number(state?.type_id) !== 4}
              />
            )}

            {Boolean(watchedOriginalAmount) && (
              <Card className="border-warning bg-soft-warning mb-3">
                <CardBody className="py-3">
                  <div className="d-flex align-items-start">
                    <div>
                      <div className="text-700 fs--1">
                        El monto que se guardará en el sistema será el equivalente en {getCurrencyName(baseCurrency.iso)} {baseCurrency.symbol}.
                        El valor mostrado arriba es solo la referencia original ingresada por el usuario.
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}

            {!commonCost && (
              <Row
                form
                className="align-items-center justify-content-center justify-content-md-end"
              >
                <ButtonIcon
                  color="falcon-default"
                  className="my-2 mx-0 mx-md-2 m-md-0 px-5 mr-2"
                  icon={faUndo}
                  iconAlign="right"
                  transform="down-1 shrink-4"
                  onClick={() =>
                    history.push({
                      pathname: `/dashboard/${moduleBase}/${id}/common-costs`,
                      state: {
                        type_id: state.type_id,
                        type_name: state.type_name,
                      },
                    })
                  }
                >
                  Volver
                </ButtonIcon>
                <ButtonIcon
                  data-testid="submit-button"
                  color="primary"
                  className="my-2 mx-0 mx-md-2 m-md-0 px-5"
                  type="submit"
                  icon={faSave}
                  iconAlign="right"
                  onClick={handleSubmit(handleFormSubmit)}
                  disabled={isSubmitting || isRateLoading}
                  transform="down-1 shrink-4"
                >
                  Guardar
                </ButtonIcon>
              </Row>
            )}
          </CardBody>
        </Fragment>
      </PreloadState>
      <CreateExhangeRateModal
        isOpen={isRateModalOpen}
        setIsOpen={setIsRateModalOpen}
        currency={selectedCurrencyData || { name: "", iso: "", id: null }}
        baseCurrency={baseCurrency}
        mutate={bumpRateRefreshTick}
        condominiumId={condominiumId || selectedCondominium}
        suggestedValue={activeExchangeRate}
      />
    </Card>
  );
};

export default withRedirect(CreateCommonCostsForm);
